// NOTE: This component wraps Helmet and requires the programmer, through propTypes, to, for example, specify all the
//  sub-properties of `og` if they would like to set `og`; it does not, however require them to set the `og` data in
//  the first place.
// It also contains defaults for meta title, `og:type`, and `twitter:site` and sets the pattern for meta titles, i.e.
//  'The Page Title | The Black Tux'.

import React from 'react'
import Helmet from 'react-helmet'
import PropTypes from 'prop-types'

const add = (meta, key, name, content) => {
  if (content && name && key) {
    meta.push({
      [key]: name,
      content,
    })
  }

  return meta
}

const Head = ({
  canonicalHref,
  description,
  image,
  nonIndexable,
  nonIndexableAndNoFollow,
  og,
  script,
  title,
  twitter,
  url,
}) => {
  const meta = []
  // add each meta tag if:
  //    (A) it is defined and
  //    (B) if its containing group was set
  add(meta, 'name', 'description', description)
  add(meta, 'property', 'og:url', url) // excepted from the og block because it's set automatically
  if (og) {
    add(meta, 'property', 'og:type', 'website')
    add(meta, 'property', 'og:title', og.title)
    add(meta, 'property', 'og:description', og.description)
    add(meta, 'property', 'og:image', og.image.url)
    add(meta, 'property', 'og:image:secure_url', og.image.urlSecure)
    add(meta, 'property', 'og:image:type', og.image.type)
    add(meta, 'property', 'og:image:width', og.image.width)
    add(meta, 'property', 'og:image:height', og.image.height)
  }
  if (twitter) {
    add(meta, 'name', 'twitter:card', twitter.card)
    add(meta, 'name', 'twitter:image', twitter.image)
    add(meta, 'name', 'twitter:site', '@theblacktux')
    add(meta, 'name', 'twitter:title', twitter.title)
    add(meta, 'name', 'twitter:description', twitter.description)
  }
  if (nonIndexable) {
    add(meta, 'name', 'robots', 'noindex,follow')
  }
  // "noindex" tag tells search engines not to include the page in search results
  // "nofollow" tag tells search engines not to follow the links on that page
  if (nonIndexableAndNoFollow) {
    add(meta, 'name', 'robots', 'noindex,nofollow')
  }

  const links = []
  if (canonicalHref) {
    links.push({rel: 'canonical', href: canonicalHref})
  }
  if (image) {
    links.push({rel: 'image_src', href: image.url, type: image.type})
  }

  return (
    <Helmet
      link={links}
      meta={meta}
      script={
        script && [
          {
            ...script,
            ...{innerHTML: JSON.stringify(script.innerHTML)},
          },
        ]
      }
      // set title if received in props and add suffix
      title={title ? `${title} | The Black Tux` : null}
    />
    // Don't add children to <Helmet> or you may trigger a bug: https://github.com/nfl/react-helmet/issues/441
  )
}

Head.defaultProps = {
  nonIndexable: false,
  nonIndexableAndNoFollow: false,
}

// NOTE: The way these propTypes are written, no top-level prop is required. However, if you wish
//  to set image, og, or twitter, you must set each of its sub-properties!
Head.propTypes = {
  canonicalHref: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.shape({
    type: PropTypes.oneOf(['image/jpeg', 'image/png']).isRequired,
    url: PropTypes.string.isRequired,
  }),
  nonIndexable: PropTypes.bool.isRequired,
  nonIndexableAndNoFollow: PropTypes.bool.isRequired,
  og: PropTypes.shape({
    description: PropTypes.string.isRequired,
    image: PropTypes.shape({
      height: PropTypes.number.isRequired,
      type: PropTypes.oneOf(['image/jpeg', 'image/png']).isRequired,
      url: PropTypes.string.isRequired,
      urlSecure: PropTypes.string.isRequired,
      width: PropTypes.number.isRequired,
    }).isRequired,
    title: PropTypes.string.isRequired,
  }),
  script: PropTypes.shape({
    innerHTML: PropTypes.shape({}),
    type: PropTypes.string,
  }),
  title: PropTypes.string,
  twitter: PropTypes.shape({
    card: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  url: PropTypes.string,
}

export default Head
